/**
 * CameraSignalBanner — a faixa "câmera sem imagem" no topo de todas as telas
 * CI Sistemas de Informática Ltda. — AcessoiD, Onda 115
 *
 * Em 25/09/2026 a entrada do Ipê Clube ficou mais de três horas sem mandar
 * imagem e nenhuma tela avisou: a saúde do sistema só aparecia no PDV — que o
 * clube não tem — e no painel do Monitoramento, que precisa ser aberto. Quem
 * percebeu foi o Iatagã, olhando a tela.
 *
 * Esta faixa aparece sozinha, em qualquer tela, para qualquer usuário, enquanto
 * houver câmera ativa sem imagem há mais que "Câmera sem sinal" (Configurações
 * → Vigias e alertas, 120 s). Diz qual câmera, há quanto tempo, a causa
 * provável — testada pelo servidor — e o que verificar. Some quando a câmera
 * volta. "Ocultar" vale até mudar o conjunto de câmeras sem imagem: se OUTRA
 * câmera cair, a faixa volta.
 *
 * O texto vem pronto do servidor (`camera_signal` em /api/system/health): a
 * tela não decide o que é grave, como na contingência do app da guarita.
 */
import React, { useEffect, useState } from 'react';
import useSystemHealth from '../hooks/useSystemHealth';
import '../styles/CameraSignalBanner.css';

const CHAVE_OCULTO = 'csb-oculto';
const TIPO = { entrada: 'entrada', saida: 'saída' };

function lerOculto() {
  try { return sessionStorage.getItem(CHAVE_OCULTO); } catch (_) { return null; }
}
function gravarOculto(valor) {
  try {
    if (valor) sessionStorage.setItem(CHAVE_OCULTO, valor);
    else sessionStorage.removeItem(CHAVE_OCULTO);
  } catch (_) { /* sem armazenamento: a faixa só não lembra que foi ocultada */ }
}

/** As câmeras sem imagem segundo a saúde do sistema, ou [] se não há alerta. */
export function camerasSemImagem(services) {
  const sinal = (services || []).find(s => s.service === 'camera_signal');
  return sinal && sinal.status === 'offline' && Array.isArray(sinal.cameras) ? sinal.cameras : [];
}

export default function CameraSignalBanner({ intervalMs = 30000 }) {
  const { services } = useSystemHealth({ intervalMs });
  const mudas = camerasSemImagem(services);
  const chave = mudas.map(c => c.id).sort((a, b) => a - b).join(',');
  const [oculto, setOculto] = useState(lerOculto);
  // Só o servidor dizendo que o sinal voltou apaga o "ocultar". Lista vazia
  // antes da primeira resposta NÃO é "tudo voltou": sem esta distinção, a faixa
  // ocultada reaparecia a cada troca de tela.
  const sinal = services.find(s => s.service === 'camera_signal');
  const voltou = !!sinal && sinal.status !== 'offline';

  // Tudo voltou: esquece o "ocultar", para a próxima queda aparecer.
  useEffect(() => {
    if (voltou && oculto) { gravarOculto(null); setOculto(null); }
  }, [voltou, oculto]);

  if (!mudas.length || oculto === chave) return null;

  const ocultar = () => { gravarOculto(chave); setOculto(chave); };

  return (
    <div className="csb-faixa" role="alert">
      <div className="csb-corpo">
        <strong className="csb-titulo">
          {mudas.length === 1 ? 'Câmera sem imagem' : `${mudas.length} câmeras sem imagem`}
        </strong>
        <ul className="csb-lista">
          {mudas.map(c => (
            <li key={c.id} className="csb-item">
              <span className="csb-camera">
                <b>{c.name}</b>
                {TIPO[c.camera_type] ? ` (${TIPO[c.camera_type]})` : ''}
                {' — '}
                {c.silent_for ? `sem imagem há ${c.silent_for}` : 'nunca mandou imagem'}
              </span>
              <span className="csb-linha">
                <span className="csb-rotulo">Causa provável:</span>{' '}
                {c.cause || 'verificando…'}
              </span>
              {c.action && (
                <span className="csb-linha">
                  <span className="csb-rotulo">O que verificar:</span> {c.action}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <button type="button" className="csb-ocultar" onClick={ocultar}>
        Ocultar
      </button>
    </div>
  );
}
